import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users,
  Tag, 
  Clock, 
  AlertCircle, 
  CheckCircle, 
  Star, 
  Plus, 
  Link as LinkIcon,
  ExternalLink,
  X
} from 'lucide-react';
import { organizationService } from '../../services/organizationService';
import { customerCategoriesService } from '../../services/customerCategoriesService';
import { CustomerCategory } from '../../types/customerCategories';
import { transformEntriesToMap } from '../../utils/categoryEntries';
import ConfirmDeleteModal from '../ConfirmDeleteModal';

interface CustomerCategoriesTabProps {
  organizationId: string;
  accessToken: string | null;
  setUserAndToken: (user: any, token: string) => void;
}

const CustomerCategoriesTab: React.FC<CustomerCategoriesTabProps> = ({
  organizationId,
  accessToken,
  setUserAndToken
}) => {
  const [connectedCategories, setConnectedCategories] = useState<CustomerCategory[]>([]);
  const [allCategories, setAllCategories] = useState<CustomerCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [categoryToDisconnect, setCategoryToDisconnect] = useState<CustomerCategory | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    if (accessToken && organizationId) {
      fetchConnectedCategories();
    }
  }, [accessToken, organizationId]);

  const fetchConnectedCategories = async () => {
    try {
      setLoading(true);
      const connectedData = await organizationService.getOrganizationCustomerCategories(
        organizationId,
        accessToken,
        setUserAndToken
      );
      
      setConnectedCategories(connectedData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch connected categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCategories = async () => {
    try {
      setModalLoading(true);
      const allData = await customerCategoriesService.getCustomerCategories(accessToken, setUserAndToken);
      setAllCategories(allData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch all categories');
    } finally {
      setModalLoading(false);
    }
  };

  const getCategoryIcon = (categoryName: string) => {
    const name = categoryName.toLowerCase();
    if (name.includes('key') || name.includes('premium')) {
      return <Star className="w-6 h-6 text-yellow-600" />;
    } else if (name.includes('regular') || name.includes('standard')) {
      return <CheckCircle className="w-6 h-6 text-blue-600" />;
    } else if (name.includes('risky') || name.includes('risk')) {
      return <AlertCircle className="w-6 h-6 text-red-600" />;
    } else if (name.includes('new')) {
      return <Clock className="w-6 h-6 text-green-600" />;
    }
    return <Users className="w-6 h-6 text-gray-600" />;
  };

  const handleConnectCategories = async () => {
    setSelectedCategoryIds(connectedCategories.map(cat => cat.id));
    setShowConnectModal(true);
    
    // Only fetch all categories if we haven't already
    if (allCategories.length === 0) {
      await fetchAllCategories();
    }
  };

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategoryIds(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSaveConnections = async () => {
    try {
      setSaving(true);
      await organizationService.connectCategoriesToOrganization(
        organizationId,
        { category_ids: selectedCategoryIds },
        accessToken,
        setUserAndToken
      );
      
      // Refresh only connected categories
      await fetchConnectedCategories();
      setShowConnectModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save connections');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnectClick = (category: CustomerCategory) => {
    setCategoryToDisconnect(category);
    setShowDisconnectModal(true);
  };

  const confirmDisconnect = async () => {
    if (!categoryToDisconnect) return;
    try {
      setDisconnecting(true);
      await organizationService.disconnectCategoryFromOrganization(
        organizationId,
        categoryToDisconnect.id,
        accessToken,
        setUserAndToken
      );
      
      // Refresh connected categories
      await fetchConnectedCategories();
      setShowDisconnectModal(false);
      setCategoryToDisconnect(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect category');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading customer categories...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Risk Profiles</h3>
          <p className="text-sm text-gray-600">
            Manage customer categorization rules for this organization
          </p>
        </div>
        <button
          onClick={handleConnectCategories}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition"
        >
          <LinkIcon className="w-4 h-4" />
          Connect Categories
        </button>
      </div>

      {/* Connected Categories */}
      {connectedCategories.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {connectedCategories.map((category) => (
            <div key={category.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  {getCategoryIcon(category.name)}
                  <div>
                    <Link
                      to={`/customer-categories/${category.id}`}
                      className="text-lg font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                    >
                      {category.name}
                    </Link>
                    {category.is_default && (
                      <p className="text-xs text-green-600 font-medium mt-0.5">DEFAULT</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <a
                    href={`/customer-categories/${category.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    title="View details in new tab"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => handleDisconnectClick(category)}
                    className="text-red-400 hover:text-red-600 transition-colors"
                    title="Disconnect category"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 line-clamp-2">{category.description}</p>
              <div className="mt-3 flex items-center justify-between">
                {(() => { const map: any = transformEntriesToMap(category.entries); return (
                  <>
                    <span className="text-xs text-gray-500">Order: {map.order_to_evaluate || 0}</span>
                    <span className="text-xs text-gray-500">{Object.keys(map).length} properties</span>
                  </>
                ); })()}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Tag className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Categories Connected</h3>
          <p className="text-gray-600">
            Connect customer categories to define how customers should be categorized for this organization.
          </p>
        </div>
      )}

      {/* Connect Categories Modal */}
      {showConnectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Connect Risk Profiles</h2>
                <button
                  onClick={() => setShowConnectModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Select the customer categories you want to connect to this organization.
              </p>
            </div>
            
            <div className="p-6 max-h-96 overflow-y-auto">
              {modalLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Loading categories...</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {allCategories.map((category) => (
                  <label
                    key={category.id}
                    className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCategoryIds.includes(category.id)}
                      onChange={() => handleCategoryToggle(category.id)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        {getCategoryIcon(category.name)}
                        <span className="font-medium text-gray-900">{category.name}</span>
                        {category.is_default && (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        {(() => { const map: any = transformEntriesToMap(category.entries); return (
                          <>
                            <span>Order: {map.order_to_evaluate || 0}</span>
                            <span>{Object.keys(map).length} properties</span>
                          </>
                        ); })()}
                      </div>
                    </div>
                  </label>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-200 flex items-center justify-between">
              {/* Select All checkbox */}
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedCategoryIds.length === allCategories.length && allCategories.length > 0}
                  onChange={() => {
                    if (selectedCategoryIds.length === allCategories.length) {
                      setSelectedCategoryIds([]);
                    } else {
                      setSelectedCategoryIds(allCategories.map(cat => cat.id));
                    }
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-900">
                  {selectedCategoryIds.length === allCategories.length && allCategories.length > 0 
                    ? 'Deselect All' 
                    : 'Select All'
                  }
                </span>
              </label>
              
              {/* Action buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowConnectModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveConnections}
                  disabled={saving || selectedCategoryIds.length === 0}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <LinkIcon className="w-4 h-4" />
                      Save Connections
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disconnect Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDisconnectModal}
        onClose={() => {
          setShowDisconnectModal(false);
          setCategoryToDisconnect(null);
        }}
        onConfirm={confirmDisconnect}
        title="Disconnect Category"
        message="Are you sure you want to disconnect this category from the organization? This will remove the category from all customer categorization rules for this organization."
        itemName={categoryToDisconnect?.name || ''}
        isLoading={disconnecting}
      />
    </div>
  );
};

export default CustomerCategoriesTab;
